/**
 * Unified search widget.
 *
 * Renders the seven-type card contract from /api/search in two places:
 * the header overlay (suggest mode) and the /search results page (full mode).
 *
 * Titles, subtitles and URLs are composed server-side. This file must not
 * build any of them — if it needs to, that is an API bug.
 *
 * Card shape: {type, id, title, subtitle, image, url, score, meta}
 * Types: campsite, product, article, trip, route, location, brand.
 *
 * Corrections applied vs. the original brief (see task-3-4-report.md):
 *  - `brand` is now handled. At most one is ever returned and the server
 *    already ranks it first, so it renders as a plain, unlabeled card
 *    ahead of the grouped sections rather than being silently dropped.
 *  - `fetchSearch` now distinguishes "request failed" (degraded: true)
 *    from "too short to search" (degraded: false, not attempted), and
 *    the overlay now checks `degraded` before falling back to the
 *    generic "no results" copy.
 */
(function () {
  'use strict';

  var API_BASE = 'https://app.trip-planner.com.au/api';
  var DEBOUNCE_MS = 250;
  var MIN_QUERY = 2;
  var UNAVAILABLE_TEXT = 'Search is unavailable right now.';
  var SESSION_KEY = 'tp-search-sid';

  // Opaque per-tab id joining a search to the result that was opened. Dies
  // with the tab, never leaves this origin, never joined to a customer.
  // Private-mode browsers throw on sessionStorage, so fall back to an
  // in-memory id rather than losing capture entirely.
  var memorySid = null;
  function sessionId() {
    var mint = function () {
      return Math.random().toString(36).slice(2) +
             Math.random().toString(36).slice(2);
    };
    try {
      var existing = window.sessionStorage.getItem(SESSION_KEY);
      if (existing) return existing;
      var fresh = mint();
      window.sessionStorage.setItem(SESSION_KEY, fresh);
      return fresh;
    } catch (e) {
      if (!memorySid) memorySid = mint();
      return memorySid;
    }
  }

  // Grouped, labeled sections rendered on /search and in the overlay.
  // `brand` is intentionally NOT a key here — it gets no section of its
  // own and no label (see renderCards), it just appears at the top.
  var GROUP_LABELS = {
    campsite: 'Campsites',
    product: 'Shop',
    article: 'Guides',
    trip: 'Trips',
    route: 'Routes',
    location: 'Places'
  };

  // Query too short / not attempted — not a failure.
  var EMPTY = { query: '', total: 0, counts: {}, results: [], smartCard: null, degraded: false };
  // Request failed (non-ok response or network error) — a real failure.
  var FAILED = { query: '', total: 0, counts: {}, results: [], smartCard: null, degraded: true };

  function fetchSearch(query, opts) {
    opts = opts || {};
    if (!query || query.trim().length < MIN_QUERY) return Promise.resolve(EMPTY);

    var params = new URLSearchParams({ q: query.trim(), mode: opts.mode || 'suggest' });
    if (opts.types) params.set('types', opts.types);
    if (opts.page) params.set('page', String(opts.page));
    if (opts.limit) params.set('limit', String(opts.limit));

    // Analytics tags. Always sent: a search the dashboard cannot attribute to
    // a surface is a search it has to drop.
    params.set('surface', opts.surface || 'storefront-header');
    params.set('brand', 'camping-australia');
    params.set('sid', sessionId());

    return fetch(API_BASE + '/search?' + params.toString(), { mode: 'cors', credentials: 'omit' })
      .then(function (r) { return r.ok ? r.json() : FAILED; })
      .catch(function () { return FAILED; });
  }

  function el(tag, className) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    return node;
  }

  /**
   * Tell the server this search led somewhere.
   *
   * sendBeacon, not fetch: the click navigates away immediately and a normal
   * request would be cancelled by the unload. Fire and forget — analytics
   * must never be able to interfere with the navigation it is measuring.
   */
  function reportClickOn(anchor, term, cardType, cardId, position, surface) {
    anchor.addEventListener('click', function () {
      try {
        var body = JSON.stringify({
          sessionId: sessionId(),
          term: term || '',
          cardType: cardType,
          cardId: String(cardId == null ? '' : cardId),
          position: position == null ? 0 : position,
          surface: surface || 'storefront-header'
        });
        navigator.sendBeacon(
          API_BASE + '/search/click',
          new Blob([body], { type: 'application/json' })
        );
      } catch (e) { /* never break the navigation */ }
    });
  }

  // Neutral 1x1 so a dead image collapses to the CSS-styled empty thumb rather
  // than the browser's broken-image glyph.
  var PLACEHOLDER =
    'data:image/svg+xml;charset=utf-8,' +
    encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="1" height="1"/>');

  function cardNode(card) {
    var a = el('a', 'us-card');
    a.href = card.url;

    var img = el('img', 'us-card__thumb');
    // Some legacy image paths are rewritten server-side to Imgix, and a share of
    // those no longer exist there. Without this the card shows the browser's
    // broken-image icon; the React renderer has had the equivalent fallback all
    // along. Cleared after firing so a failing placeholder cannot loop.
    img.onerror = function () {
      img.onerror = null;
      img.src = PLACEHOLDER;
    };
    img.src = card.image;
    img.alt = '';
    img.loading = 'lazy';
    a.appendChild(img);

    var text = el('span', 'us-card__text');

    var title = el('span', 'us-card__title');
    title.textContent = card.title;
    text.appendChild(title);

    if (card.subtitle) {
      var sub = el('span', 'us-card__subtitle');
      sub.textContent = card.subtitle;
      text.appendChild(sub);
    }

    if (card.meta && typeof card.meta.price === 'number') {
      var price = el('span', 'us-card__price');
      price.textContent = '$' + card.meta.price.toFixed(2).replace(/\.00$/, '');
      text.appendChild(price);
    }

    a.appendChild(text);

    // ONE listener here covers the typeahead, the overlay AND the /search
    // results page — all three build their cards through this function. That
    // is the whole reason to instrument here rather than at each call site:
    // the naive count of places to hook is one, the real count is three.
    reportClickOn(a, card.query, card.type, card.id, card.position, card.surface);

    return a;
  }

  /** Render cards into a container, grouped by type. Replaces all content. */
  function renderCards(container, cards, options) {
    options = options || {};
    container.innerHTML = '';

    // cardNode has no way to know which query produced it, where it ranked,
    // or which surface it is rendering on. Stamp all three here, once, rather
    // than threading three more arguments through every call.
    cards.forEach(function (c, i) {
      c.query = options.query || '';
      c.position = i;
      c.surface = options.surface || 'storefront-header';
    });

    if (!cards.length) {
      var empty = el('div', 'us-empty');
      empty.textContent = options.emptyText || 'No results found.';
      container.appendChild(empty);
      return;
    }

    if (options.grouped === false) {
      var flat = el('div', 'us-results');
      cards.forEach(function (c) { flat.appendChild(cardNode(c)); });
      container.appendChild(flat);
      return;
    }

    // `brand` — at most one, already ranked first by the server. No
    // section, no label: render it as a bare card ahead of the groups.
    var brandCards = cards.filter(function (c) { return c.type === 'brand'; });
    brandCards.forEach(function (c) { container.appendChild(cardNode(c)); });

    // Groups follow the server's ranking: a group sits where its best card
    // scored. GROUP_LABELS' declaration order is only a tie-break now.
    //
    // It used to BE the order, which meant Campsites always rendered first no
    // matter what was asked. Searching "tents" put campsites — matched on
    // description text — above the shop. The client stays free of intent
    // rules: it just stops overriding the ranking it was given.
    var declared = Object.keys(GROUP_LABELS);

    function bestScore(type) {
      return cards.reduce(function (best, c) {
        return c.type === type ? Math.max(best, Number(c.score) || 0) : best;
      }, 0);
    }

    declared.slice().sort(function (a, b) {
      return (bestScore(b) - bestScore(a)) || (declared.indexOf(a) - declared.indexOf(b));
    }).forEach(function (type) {
      var items = cards.filter(function (c) { return c.type === type; });
      if (!items.length) return;

      var group = el('div', 'us-group');
      var label = el('div', 'us-group__label');
      label.textContent = GROUP_LABELS[type];
      group.appendChild(label);

      var list = el('div', 'us-results');
      items.forEach(function (c) { list.appendChild(cardNode(c)); });
      group.appendChild(list);

      container.appendChild(group);
    });
  }

  function renderSmartCard(container, smart, options) {
    if (!smart) return;
    options = options || {};
    var a = el('a', 'us-smart');
    a.href = smart.url;
    a.textContent = smart.title;
    // Instrumented too: clicking the hire card IS the search succeeding.
    // Without this, a search whose only click was the smart card shows up in
    // the "found it, nobody opened it" table — the one the dashboard leads
    // with — as a false alarm.
    reportClickOn(a, options.query, smart.type || 'hire', '', 0,
                  options.surface || 'storefront-header');
    container.insertBefore(a, container.firstChild);
  }

  /** Header overlay — suggest mode, debounced on input. */
  function mountOverlay(input, container) {
    if (!input || !container) return;
    var timer = null;

    input.addEventListener('input', function () {
      var query = input.value;
      if (timer) clearTimeout(timer);

      if (query.trim().length < MIN_QUERY) {
        container.innerHTML = '';
        return;
      }

      timer = setTimeout(function () {
        var status = el('div', 'us-status');
        status.textContent = 'Searching…';
        container.innerHTML = '';
        container.appendChild(status);

        fetchSearch(query, { mode: 'suggest' }).then(function (data) {
          if (data.degraded) {
            var warn = el('div', 'us-empty');
            warn.textContent = UNAVAILABLE_TEXT;
            container.innerHTML = '';
            container.appendChild(warn);
            return;
          }

          renderCards(container, data.results, {
            emptyText: 'No results found.',
            query: query,
            surface: 'storefront-header'
          });
          renderSmartCard(container, data.smartCard, {
            query: query, surface: 'storefront-header'
          });

          if (data.total > 0) {
            var all = el('a', 'us-smart');
            all.href = '/search?q=' + encodeURIComponent(query);
            all.textContent = 'See all ' + data.total + ' results';
            container.appendChild(all);
          }
        });
      }, DEBOUNCE_MS);
    });
  }

  /**
   * Header search boxes — the ones customers actually type into.
   *
   * mountOverlay above binds the full-screen `.search-overlay`, but nothing in
   * the theme opens it: the only `data-action="toggle-search"` hook is that
   * overlay's own close link. So the suggest dropdown was unreachable while the
   * visible header field stayed a plain GET form that jumped straight to
   * /search. This binds the same suggest behaviour to the real inputs.
   *
   * The panel is appended to <body> and positioned `fixed` against the input's
   * bounding box rather than nested beside it — the header wrapper clips
   * overflow (the mega menu had to be moved out of it for the same reason), so
   * an absolutely-positioned child would be cut off.
   *
   * Enter with nothing highlighted deliberately falls through to the form's
   * normal submit, so /search stays the full-results escape hatch.
   */
  function mountHeaderTypeahead(input) {
    if (!input || input.getAttribute('data-us-typeahead')) return;
    input.setAttribute('data-us-typeahead', '1');
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('aria-expanded', 'false');

    var panel = el('div', 'us-typeahead');
    panel.hidden = true;
    document.body.appendChild(panel);

    var timer = null;
    var index = -1;

    function options() { return panel.querySelectorAll('.us-card, .us-smart'); }

    function place() {
      var r = input.getBoundingClientRect();
      panel.style.left = r.left + 'px';
      panel.style.top = (r.bottom + 6) + 'px';
      panel.style.width = r.width + 'px';
    }

    function open() {
      if (!panel.firstChild) return;
      place();
      panel.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    }

    function close() {
      panel.hidden = true;
      index = -1;
      input.setAttribute('aria-expanded', 'false');
    }

    function highlight(next) {
      var list = options();
      if (!list.length) return;
      if (list[index]) list[index].classList.remove('is-selected');
      index = next < 0 ? list.length - 1 : (next >= list.length ? 0 : next);
      list[index].classList.add('is-selected');
      list[index].scrollIntoView({ block: 'nearest' });
    }

    input.addEventListener('input', function () {
      var query = input.value;
      if (timer) clearTimeout(timer);
      index = -1;

      if (query.trim().length < MIN_QUERY) {
        panel.innerHTML = '';
        close();
        return;
      }

      timer = setTimeout(function () {
        var status = el('div', 'us-status');
        status.textContent = 'Searching…';
        panel.innerHTML = '';
        panel.appendChild(status);
        open();

        fetchSearch(query, { mode: 'suggest' }).then(function (data) {
          // A slower earlier request must not overwrite a newer query's results.
          if (input.value.trim() !== query.trim()) return;
          panel.innerHTML = '';

          if (data.degraded) {
            var warn = el('div', 'us-empty');
            warn.textContent = UNAVAILABLE_TEXT;
            panel.appendChild(warn);
            open();
            return;
          }

          renderCards(panel, data.results, {
            emptyText: 'No results found.',
            query: query,
            surface: 'storefront-header'
          });
          renderSmartCard(panel, data.smartCard, {
            query: query, surface: 'storefront-header'
          });

          if (data.total > 0) {
            var all = el('a', 'us-smart');
            all.href = '/search?q=' + encodeURIComponent(query);
            all.textContent = 'See all ' + data.total + ' results';
            panel.appendChild(all);
          }
          open();
        });
      }, DEBOUNCE_MS);
    });

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { close(); return; }
      if (panel.hidden) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        highlight(index + 1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        highlight(index - 1);
      } else if (e.key === 'Enter') {
        var list = options();
        if (index >= 0 && list[index]) {
          e.preventDefault();
          window.location.href = list[index].href;
        }
      }
    });

    input.addEventListener('focus', function () { open(); });

    // mousedown, not blur: blur fires before a result's click lands, which
    // would tear the panel down and swallow the navigation.
    document.addEventListener('mousedown', function (e) {
      if (e.target === input || panel.contains(e.target)) return;
      close();
    });

    window.addEventListener('resize', function () { if (!panel.hidden) place(); });
    window.addEventListener('scroll', function () { if (!panel.hidden) place(); }, true);
  }

  /**
   * /search results page. The Shop tab stays server-rendered by Shopify —
   * it already has filtering, sorting and pagination that this would only
   * reimplement worse. Every other tab is fetched here.
   */
  function mountResults(root) {
    if (!root) return;
    var query = root.getAttribute('data-query') || '';
    if (!query) return;

    var tabs = root.querySelectorAll('[data-us-tab]');
    var panels = root.querySelectorAll('[data-us-panel]');
    var loaded = {};

    function show(type) {
      Array.prototype.forEach.call(tabs, function (t) {
        t.setAttribute('aria-selected', String(t.getAttribute('data-us-tab') === type));
      });
      Array.prototype.forEach.call(panels, function (p) {
        p.hidden = p.getAttribute('data-us-panel') !== type;
      });
      if (type !== 'product') load(type);
    }

    function load(type) {
      if (loaded[type]) return;
      loaded[type] = true;

      var panel = root.querySelector('[data-us-panel="' + type + '"]');
      if (!panel) return;

      var status = el('div', 'us-status');
      status.textContent = 'Searching…';
      panel.appendChild(status);

      var opts = { mode: 'full', limit: 50, surface: 'storefront-results' };
      if (type !== 'all') opts.types = type;

      fetchSearch(query, opts).then(function (data) {
        panel.innerHTML = '';
        if (data.degraded) {
          var warn = el('div', 'us-empty');
          warn.textContent = UNAVAILABLE_TEXT;
          panel.appendChild(warn);
          return;
        }
        renderCards(panel, data.results, {
          grouped: type === 'all',
          query: query,
          surface: 'storefront-results'
        });
        if (type === 'all') {
          renderSmartCard(panel, data.smartCard, {
            query: query, surface: 'storefront-results'
          });
        }
        updateCounts(data.counts, data.total);
      });
    }

    function updateCounts(counts, total) {
      Array.prototype.forEach.call(tabs, function (t) {
        var type = t.getAttribute('data-us-tab');
        var slot = t.querySelector('.us-tab__count');
        if (!slot) return;
        // The Shop tab's count is Shopify's own, server-rendered — leave it.
        if (type === 'product') return;
        var n = type === 'all' ? total : (counts[type] || 0);
        slot.textContent = n ? '(' + n + ')' : '';
      });
    }

    Array.prototype.forEach.call(tabs, function (t) {
      t.addEventListener('click', function () { show(t.getAttribute('data-us-tab')); });
    });

    show('all');
  }

  window.UnifiedSearch = {
    fetchSearch: fetchSearch,
    renderCards: renderCards,
    mountOverlay: mountOverlay,
    mountHeaderTypeahead: mountHeaderTypeahead,
    mountResults: mountResults
  };

  document.addEventListener('DOMContentLoaded', function () {
    // Desktop header field and the mobile one. Kept as a selector list so a
    // third search box anywhere in the theme only needs its class adding here.
    Array.prototype.forEach.call(
      document.querySelectorAll(
        '.search-input-wrapper input[name="q"], .global-mobile-search-input'
      ),
      function (input) { mountHeaderTypeahead(input); }
    );

    // Left mounted so the overlay still works if anything ever opens it.
    mountOverlay(
      document.querySelector('.search-overlay__input'),
      document.querySelector('[data-us-overlay-results]')
    );
    mountResults(document.querySelector('[data-us-results]'));
  });
})();
